import type { SparkWeekPreview } from '@/core/data/spark/spark.interface';
import { gambits } from '@/core/gambits';
import { createLogger } from '@/core/helpers/logger.helper';
import { firestore } from '@/core/network/firebase';
import { doc, onSnapshot, Timestamp, type Unsubscribe } from 'firebase/firestore';
import { defineStore } from 'pinia';
import { ref } from 'vue';

const REFRESH_DEBOUNCE_MS = 1500;

const logger = createLogger('SparkWeekPreviewStore');

interface LocationSparkActivity {
  sparkActivityAt?: Timestamp;
}

export const useSparkWeekPreviewStore = defineStore('sparkWeekPreview', () => {
  const preview = ref<SparkWeekPreview | null>(null);
  const isLoading = ref(false);
  const isError = ref(false);
  const from = ref<string | null>(null);

  const locationUnsubscribes = new Map<string, Unsubscribe>();
  let subscribedLocationIds: string[] = [];
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;
  let inFlight: Promise<void> | null = null;
  let hasQueuedRefresh = false;

  async function fetch(fromDate: string, options: { silent?: boolean } = {}): Promise<void> {
    if (from.value !== fromDate) {
      from.value = fromDate;
      preview.value = null;
    }

    if (inFlight) {
      hasQueuedRefresh = true;
      return inFlight;
    }

    /* A cached preview stays on screen while it revalidates, so only a cold
       load shows the skeleton. */
    if (!options.silent && !preview.value) {
      isLoading.value = true;
    }

    inFlight = (async () => {
      const response = await gambits.sparkService.getWeekPreview(fromDate);

      if (from.value !== fromDate) {
        return;
      }

      if (response.data) {
        isError.value = false;
        if (response.data.fingerprint !== preview.value?.fingerprint) {
          preview.value = response.data;
          syncLocationListeners();
        }
      } else if (!preview.value) {
        isError.value = true;
      }
    })();

    try {
      await inFlight;
    } catch (error) {
      logger.warn('Failed to load the Sparks week preview', { from: fromDate, error });
      isError.value = !preview.value;
    } finally {
      inFlight = null;
      isLoading.value = false;
    }

    if (hasQueuedRefresh) {
      hasQueuedRefresh = false;
      await refresh();
    }
  }

  function refresh(): Promise<void> {
    return from.value ? fetch(from.value, { silent: true }) : Promise.resolve();
  }

  function scheduleRefresh(): void {
    if (refreshTimer) {
      clearTimeout(refreshTimer);
    }

    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      void refresh();
    }, REFRESH_DEBOUNCE_MS);
  }

  /* Watches the player's spots plus every spot already in the preview, since
     invitations and votes can sit outside the selected spots. */
  function subscribe(locationIds: string[]): void {
    subscribedLocationIds = locationIds;
    syncLocationListeners();
  }

  function syncLocationListeners(): void {
    const nextLocationIds = new Set([
      ...subscribedLocationIds,
      ...(preview.value?.items || [])
        .map((item) => item.location?.id)
        .filter((locationId): locationId is string => !!locationId),
    ]);

    for (const [locationId, unsubscribeLocation] of locationUnsubscribes) {
      if (!nextLocationIds.has(locationId)) {
        unsubscribeLocation();
        locationUnsubscribes.delete(locationId);
      }
    }

    nextLocationIds.forEach((locationId) => {
      if (locationUnsubscribes.has(locationId)) {
        return;
      }

      let isBootstrapSnapshot = true;
      let lastActivityAt: number | null = null;

      const unsubscribeLocation = onSnapshot(doc(firestore, 'locations', locationId), (snapshot) => {
        const activityAt = (snapshot.data() as LocationSparkActivity | undefined)?.sparkActivityAt?.toMillis() ?? null;

        if (isBootstrapSnapshot) {
          isBootstrapSnapshot = false;
          lastActivityAt = activityAt;
          return;
        }

        /* Reconnects replay cached docs, and check-ins or edits bump other fields
           on the same doc; neither is preview news. */
        if (snapshot.metadata.fromCache || activityAt === lastActivityAt) {
          return;
        }

        lastActivityAt = activityAt;
        scheduleRefresh();
      });

      locationUnsubscribes.set(locationId, unsubscribeLocation);
    });
  }

  function unsubscribe(): void {
    locationUnsubscribes.forEach((unsubscribeLocation) => unsubscribeLocation());
    locationUnsubscribes.clear();
    subscribedLocationIds = [];

    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
  }

  function reset(): void {
    unsubscribe();
    preview.value = null;
    from.value = null;
    isError.value = false;
  }

  return {
    preview,
    isLoading,
    isError,
    from,
    fetch,
    refresh,
    subscribe,
    unsubscribe,
    reset,
  };
});
